import { Button, Select, SelectOption } from "@rio-cloud/rio-uikit";
import { useNavigate } from "react-router";
import OccurrenceVehicleSummaryCard from "../occurrenceVehicleSummaryCard";
import { useSupportByOccurrence } from "../../../../../../hooks/useSupportByOccurrence";
import { Occurrence } from "../../../../../../models/occurrence";
import { typeSubjectOccorenceIds } from "../../../../../../components/support/constants";
import SupportItem from "./SupportItem";
import { useIntl } from "react-intl";
import { useMemo } from "react";

interface Props {
  occurrence: Occurrence;
  persona?: "MANAGER" | "TOWER" | "CONSULTANT" | "PARTNERSHIP";
}

const getPersonaBasePath = () => `/${window.location.pathname.split("/").filter(Boolean)[0] ?? ""}`;

const SupportTab = ({ occurrence }: Props) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const subjectOptions: SelectOption[] = useMemo(
    () => [
      { id: "", label: intl.formatMessage({ id: "intl-msg:support.all" }) },
      ...Object.entries(typeSubjectOccorenceIds).map(([id, labelId]) => ({ id, label: intl.formatMessage({ id: labelId }) })),
    ],
    [intl]
  );
  const {
    openOccurrences,
    finishedOccurrences,
    loading,
    searchTerm,
    setSearchTerm,
    subject,
    setSubject,
    responsible,
    setResponsible,
  } = useSupportByOccurrence(occurrence.id);

  const goToSupportDetails = (id: number | string) => {
    navigate(`${getPersonaBasePath()}/support/details?id=${id}`);
  };

  const goToNewSupportCall = () => {
    navigate(`${getPersonaBasePath()}/support/new-support-call?mainOccurrenceId=${occurrence.id}`);
  };

  return (
    <div>
      <div className="text-size-h4 margin-bottom-5">{intl.formatMessage({ id: "intl-msg:support.title" })}</div>
      <div className="text-color-gray margin-bottom-15">
        {intl.formatMessage({ id: "intl-msg:support.description" })}
      </div>

      <OccurrenceVehicleSummaryCard occurrence={occurrence} />

      <hr className="border-color-gray-light border-bottom-solid margin-bottom-20" />

      <div className="text-medium margin-bottom-10">{intl.formatMessage({ id: "intl-msg:support.all-assignments" })}</div>

      <Button bsStyle={Button.PRIMARY} iconName="rioglyph-plus" onClick={goToNewSupportCall} className="margin-bottom-15">
        {intl.formatMessage({ id: "intl-msg:support.new-request" })}
      </Button>

      <input
        type="text"
        className="form-control margin-bottom-10"
        placeholder={intl.formatMessage({ id: "intl-msg:support.search-placeholder" })}
        value={searchTerm}
        onChange={(event) => setSearchTerm(event.target.value)}
      />

      <div className="display-flex gap-10 margin-bottom-15">
        <div className="flex-1-1">
          <label className="text-size-12">{intl.formatMessage({ id: "intl-msg:support.subject" })}</label>
          <Select
            id="supportSubjectFilter"
            placeholder={intl.formatMessage({ id: "intl-msg:support.all" })}
            value={[subject]}
            options={subjectOptions}
            useFilter
            onChange={(selected) => setSubject(selected?.id ?? "")}
          />
        </div>
        <div className="flex-1-1">
          <label className="text-size-12">{intl.formatMessage({ id: "intl-msg:support.responsible" })}</label>
          <input
            type="text"
            className="form-control"
            value={responsible}
            onChange={(event) => setResponsible(event.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center padding-20">{intl.formatMessage({ id: "intl-msg:support.loading" })}</div>
      ) : (
        <>
          <div className="text-medium margin-bottom-10">{intl.formatMessage({ id: "intl-msg:support.open-assignments" })}</div>
          {openOccurrences.length === 0 && (
            <div className="text-color-gray text-size-12 margin-bottom-15">{intl.formatMessage({ id: "intl-msg:support.no-open-assignments" })}</div>
          )}
          {openOccurrences.map((item) => (
            <SupportItem key={item.id} data={item} onClick={() => goToSupportDetails(item.id)} />
          ))}

          <hr className="border-color-gray-light border-bottom-solid margin-top-10 margin-bottom-15" />

          <div className="text-medium margin-bottom-10">{intl.formatMessage({ id: "intl-msg:support.finished-assignments" })}</div>
          {finishedOccurrences.length === 0 && (
            <div className="text-color-gray text-size-12">{intl.formatMessage({ id: "intl-msg:support.no-finished-assignments" })}</div>
          )}
          {finishedOccurrences.map((item) => (
            <SupportItem key={item.id} data={item} onClick={() => goToSupportDetails(item.id)} />
          ))}
        </>
      )}
    </div>
  );
};

export default SupportTab;
